/**
 * Platform items for display: summaries for a set of item ids (icons in the
 * treasure editor), and a debounced item search (the item picker).
 *
 * Summaries are shared by every caller for the page's lifetime (items only
 * change when the platform publishes a release). A failed lookup is not cached,
 * so a later render retries; an unknown id resolves to `null`.
 */

import { useEffect, useMemo, useState } from 'react';
import type { PlatformItemPage, PlatformItemSearchParams, PlatformItemSummary } from '@swtorbit/shared-socket-events';
import { fetchPlatformItem, searchPlatformItems } from '../utils/platformApi';

const summaries = new Map<string, Promise<PlatformItemSummary | null>>();

function loadSummary(id: string): Promise<PlatformItemSummary | null> {
  let pending = summaries.get(id);
  if (!pending) {
    pending = fetchPlatformItem(Number(id)).catch((error: unknown) => {
      // `not_found` is an answer; anything else is retried next time.
      if (error instanceof Error && error.message === 'not_found') return null;
      summaries.delete(id);
      throw error;
    });
    summaries.set(id, pending);
  }
  return pending;
}

/** Summaries of the given platform item ids, keyed by id (as strings). Missing until loaded. */
export function usePlatformItemSummaries(ids: readonly (string | undefined)[]): Map<string, PlatformItemSummary> {
  const key = useMemo(() => [...new Set(ids.filter((id): id is string => !!id))].sort().join(','), [ids]);
  const [loaded, setLoaded] = useState<Map<string, PlatformItemSummary>>(new Map());

  useEffect(() => {
    if (!key) return;
    let live = true;
    const wanted = key.split(',');
    Promise.allSettled(wanted.map(loadSummary)).then((results) => {
      if (!live) return;
      setLoaded((prev) => {
        const next = new Map(prev);
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value) next.set(wanted[i], r.value);
        });
        return next;
      });
    });
    return () => { live = false; };
  }, [key]);

  return loaded;
}

export interface UsePlatformItemSearch {
  page: PlatformItemPage | null;
  loading: boolean;
  error: string | null;
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Search platform items as the admin types. Nothing is fetched for an empty `q`.
 * Debounced, and a newer search cancels an older one still in flight.
 */
export function usePlatformItemSearch(params: PlatformItemSearchParams): UsePlatformItemSearch {
  const [state, setState] = useState<UsePlatformItemSearch>({ page: null, loading: false, error: null });
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    const current = JSON.parse(paramsKey) as PlatformItemSearchParams;
    if (!current.q?.trim()) {
      setState({ page: null, loading: false, error: null });
      return;
    }
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    const timer = setTimeout(() => {
      searchPlatformItems(current, controller.signal)
        .then((page) => setState({ page, loading: false, error: null }))
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          setState({ page: null, loading: false, error: error instanceof Error ? error.message : 'Search failed' });
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [paramsKey]);

  return state;
}
